import { useCallback, useEffect, useRef } from 'react';

/**
 * useSwipe — horizontal swipe paging for photo surfaces.
 *
 * Extracted from UnifiedCard/PhotoCarousel, which was the only photo surface in
 * the app that supported a gesture. Every other one (lightboxes, hero galleries,
 * memory cards) navigated by arrow button alone, which is why those arrows had
 * to sit permanently visible at high opacity on mobile — they were the only way
 * through the set, so they could never recede. Give a surface this hook and its
 * arrows become a pointer affordance again, free to hide behind a hover.
 *
 * Three behaviours the original inline handler lacked, all of which matter on a
 * phone:
 *
 *  - **Direction gating.** The original compared only the X delta, so a vertical
 *    feed scroll that drifted 50px sideways paged the photo out from under the
 *    finger. A gesture now has to be meaningfully more horizontal than vertical
 *    (DIRECTION_RATIO) before it counts.
 *  - **Multi-touch bail-out.** A second finger means a pinch-zoom, not a page.
 *    Relevant in the lightbox, where zooming a photo is expected.
 *  - **Zoom bail-out.** Once the viewport is pinch-zoomed, a horizontal drag is
 *    the user panning across the photo, not asking for the next one.
 *
 * Position is held in refs rather than state: the original setState-per-touchmove
 * re-rendered the whole carousel on every frame of every drag. The same
 * constraint shapes `dragFollow` below — it drives the DOM through CSS custom
 * properties set imperatively on the element, so a 60-frame drag causes zero
 * React renders.
 *
 * Returns props to spread onto the swipeable element:
 *   const swipe = useSwipe({ onSwipeLeft: next, onSwipeRight: prev, enabled: n > 1 });
 *   <div {...swipe}>
 *
 * Spread it onto a bare DOM element, and if that element needs a `style` of its
 * own, merge rather than replace (`style={{ ...swipe.style, ... }}`) — the
 * returned `style` carries the touch-action below, and losing it silently
 * reopens the double-paging bug.
 *
 * ── dragFollow mode (opt-in) ────────────────────────────────────────────────
 *
 * `dragFollow: true` upgrades the discrete end-of-gesture decision into direct
 * manipulation, YouTube-style: the surface depresses slightly under the finger
 * and the slides track it, snapping or springing back on release. The hook
 * writes three CSS custom properties on the element it is spread onto:
 *
 *   --swipe-x        px the finger has dragged (rubber-banded at the edges).
 *                    Slides opt in by using it in their transform:
 *                    `translateX(calc(<base>% + var(--swipe-x, 0px)))`
 *   --swipe-snap-ms  '0ms' while a drag is live, absent otherwise. Slides use
 *                    it as `transitionDuration: 'var(--swipe-snap-ms, 300ms)'`
 *                    so they stick to the finger mid-drag but animate the
 *                    settle. Both properties are removed in the same task that
 *                    fires the page callback, so the settle animates from the
 *                    dragged position to the new resting one.
 *   --swipe-press    scale while pressed. Applied after a short delay so a
 *                    scroll passing over the surface doesn't flash it,
 *                    immediately once a drag locks in, and released on
 *                    end/cancel. The hook only SETS the variable — the
 *                    consumer renders it, on a DESCENDANT:
 *                    `transform: scale(var(--swipe-press, 1))` plus its own
 *                    transition. It must NOT go on the element the handlers
 *                    are spread over: a transform there (even scale(1))
 *                    makes that element a stacking context, flattening its
 *                    z-indexed chrome into one atom — which is how a first
 *                    draft of this mode sank the carousel's dot row under
 *                    UnifiedCard's sibling z-[2] bottom gradient.
 *
 * A release pages when it either travels `minDistance` or is a genuine flick —
 * short but fast, in the direction of travel. `canSwipeLeft`/`canSwipeRight`
 * tell the hook where the set ends: dragging past an end keeps following the
 * finger at EDGE_RESISTANCE so the surface feels attached, not broken, and the
 * release springs back instead of paging.
 *
 * Consumers that don't opt in are byte-identical to the pre-dragFollow hook.
 *
 * @param {function} onSwipeLeft  - finger moves left; advance (next photo)
 * @param {function} onSwipeRight - finger moves right; go back (previous photo)
 * @param {boolean}  [enabled]    - false makes every handler a no-op (single photo)
 * @param {number}   [minDistance] - px of horizontal travel before it counts
 * @param {boolean}  [guardNestedScroll] - see SWIPE_TOUCH_ACTION; pass false only
 *   on a surface that can never sit inside a horizontal scroller
 * @param {boolean}  [dragFollow]  - slides track the finger; see above
 * @param {boolean}  [canSwipeLeft]  - dragFollow only: a next slide exists
 * @param {boolean}  [canSwipeRight] - dragFollow only: a previous slide exists
 */

// Below this, a tap that drifts a few pixels would page the photo. 50px is the
// value the carousel shipped with and felt right in use.
const MIN_SWIPE_DISTANCE = 50;

// How much more horizontal than vertical a gesture must be to page rather than
// scroll. 1.2 is deliberately forgiving — a swipe across a photo is rarely
// perfectly level, and demanding a clean horizontal makes the surface feel dead.
const DIRECTION_RATIO = 1.2;

// dragFollow: travel before the gesture commits to an axis. Below this nothing
// follows — a tap with a few px of jitter must not nudge the slide.
const LOCK_AXIS_PX = 8;

// dragFollow: a release shorter than minDistance still pages when it's a real
// flick — at least this many px of net travel, moving at or above this speed,
// in the direction of the travel. 0.5 px/ms ≈ a brisk 30px in one frame.
const FLICK_MIN_PX = 20;
const FLICK_VELOCITY = 0.5;

// A flick is only a flick if the finger was still moving when it lifted.
// Velocity comes from the last two touchmove samples, and a stationary finger
// generates no touchmoves — without this cutoff, drag-fast-then-hold-then-lift
// kept the stale fast samples and paged, when holding still is precisely the
// "changed my mind" gesture.
const FLICK_MAX_SAMPLE_AGE_MS = 100;

// dragFollow: dragging past the first/last slide keeps following the finger at
// this fraction, so the edge feels like a rubber band rather than a wall.
const EDGE_RESISTANCE = 0.3;

// dragFollow: the press depression waits a beat before appearing so a vertical
// scroll whose touch merely passes through the surface doesn't flash every
// card it crosses. A drag that locks horizontal applies it immediately.
const PRESS_DELAY_MS = 80;
const PRESS_SCALE = '0.97';

// Declares the gesture to the BROWSER, not just to React — without it a swipe
// surface nested in a horizontally-scrollable ancestor is paged twice over. The
// Home and Destination feed strips are `overflow-x-auto snap-x snap-mandatory`
// with 80vw cards (Dashboard/HorizontalScrollSection), so one flick across a
// card's photo was spent scrolling the strip AND measured here: the list
// advanced one card and the photo advanced one slide off a single gesture.
// `pan-y` withdraws horizontal panning from gestures that start on the surface,
// so no ancestor can claim them; the strip still scrolls from the card's
// content and action rows, which are outside this element. Vertical page scroll
// is untouched, and `pinch-zoom` keeps the zoom the multi-touch bail-out
// assumes.
//
// It has to be CSS. React registers touchmove as a PASSIVE listener at the
// root, so `preventDefault()` inside an onTouchMove prop is a silent no-op —
// the only other lever is a manually attached non-passive listener.
//
// **Do not "simplify" this to `manipulation`.** It looks like the tidier value
// and it is wrong twice over: it restores `pan-x`, handing the gesture straight
// back to the ancestor strip and undoing the fix, and it is one of the two
// values (with `none`) that WebKit checks for to disable double-tap-to-zoom,
// so it would break a gesture `pan-y pinch-zoom` leaves alone.
//
// Module-level so the object identity is stable across renders.
//
// Deliberately the ONLY style this hook ever returns, dragFollow included.
// The press depression is NOT added here: a transform on this element — even
// a resting scale(1) — creates a stacking context, and the carousel's root
// carries z-indexed chrome (the dot row) that must keep out-ranking sibling
// overlays outside it. Consumers render the depression on an inner element
// instead; the custom property inherits (see the --swipe-press docs above).
const SWIPE_TOUCH_ACTION = { touchAction: 'pan-y pinch-zoom' };

// ── The reading half of the dragFollow contract ─────────────────────────────
//
// The hook WRITES --swipe-x / --swipe-snap-ms / --swipe-press; a surface has to
// render them or dragFollow does nothing visible. These two helpers are that
// rendering, exported from here so every dragFollow surface expresses the same
// gesture instead of each re-deriving the formulas from the docblock above —
// which is how the settle duration and the press scale would quietly drift
// apart between surfaces.
//
// Slides: absolutely-positioned siblings offset by whole widths, so the
// neighbours sit just off-screen and slide in with the finger.
// `transitionDuration` MUST be the var, not a Tailwind duration class: it falls
// back to the settle time and is forced to 0ms only while a drag is live, which
// is what makes the slide stick to the finger and still animate the release.
export const swipeSlideStyle = (index, currentIndex) => ({
  transform: `translateX(calc(${(index - currentIndex) * 100}% + var(--swipe-x, 0px)))`,
  transitionDuration: 'var(--swipe-snap-ms, 300ms)'
});

// The press depression. Spread this on an inner wrapper around the slides,
// NEVER on the element the handlers are spread over — a transform there (even
// a resting scale(1)) makes that element a stacking context and flattens its
// z-indexed chrome into one atom. The variable inherits down to the wrapper.
export const SWIPE_PRESS_STYLE = {
  transform: 'scale(var(--swipe-press, 1))',
  transition: 'transform 200ms ease'
};

// Absent in jsdom and in browsers without the visual-viewport API; that reads
// as "not zoomed". The tolerance absorbs the float noise some platforms report
// after a rotation or a keyboard dismissal — on an exact `> 1` a surface could
// latch into never paging again over a scale of 1.0000001.
const ZOOM_TOLERANCE = 0.05;

function isViewportZoomed() {
  if (typeof window === 'undefined') return false;
  const scale = window.visualViewport?.scale;
  return typeof scale === 'number' && scale > 1 + ZOOM_TOLERANCE;
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

// Prefer the EVENT's timestamp over the handler's execution time: the flick
// staleness cutoff must measure the gesture, not the scheduler. Under
// main-thread jank (an image decode, a feed re-render) a touchend handler can
// run 100ms+ after the finger lifted, and clocking the samples at handler-run
// time would read a genuine flick as stale and drop the page. Event
// timestamps share performance.now()'s timeline in modern browsers; the
// fallback keeps plain-object test events on the mockable clock.
const eventTime = (e) => (
  e && typeof e.timeStamp === 'number' && e.timeStamp > 0 ? e.timeStamp : now()
);

export default function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  minDistance = MIN_SWIPE_DISTANCE,
  guardNestedScroll = true,
  dragFollow = false,
  canSwipeLeft = true,
  canSwipeRight = true
} = {}) {
  const startRef = useRef(null);
  const endRef = useRef(null);

  // dragFollow gesture state. elRef holds the DOM node the gesture started on
  // (e.currentTarget is only valid during the handler); axisRef is null until
  // the gesture commits to 'x' (follow) or 'y' (leave it to the scroller).
  // The two move samples give a release velocity for the flick test.
  const elRef = useRef(null);
  const axisRef = useRef(null);
  const prevMoveRef = useRef(null);
  const lastMoveRef = useRef(null);
  const pressTimerRef = useRef(null);

  // Latest props live in a ref so the returned handlers stay referentially
  // stable across renders and still never fire a stale callback — the
  // callbacks close over the current slide index at almost every call site.
  const latest = useRef({ onSwipeLeft, onSwipeRight, enabled, minDistance, dragFollow, canSwipeLeft, canSwipeRight });
  latest.current = { onSwipeLeft, onSwipeRight, enabled, minDistance, dragFollow, canSwipeLeft, canSwipeRight };

  // Everything a live drag has done to the DOM, undone. Runs on end, cancel,
  // and multi-touch abandon — all three must leave no property behind, or a
  // card stays depressed / half-dragged with no finger on it.
  const resetDragArtifacts = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    const el = elRef.current;
    if (el) {
      el.style.removeProperty('--swipe-x');
      el.style.removeProperty('--swipe-snap-ms');
      el.style.removeProperty('--swipe-press');
    }
    elRef.current = null;
    axisRef.current = null;
    prevMoveRef.current = null;
    lastMoveRef.current = null;
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!latest.current.enabled) return;
    // A second finger is a pinch, not a page — abandon the gesture entirely
    // rather than tracking one finger of it.
    if (e.touches.length > 1) {
      startRef.current = null;
      if (latest.current.dragFollow) resetDragArtifacts();
      return;
    }
    const touch = e.targetTouches[0];
    startRef.current = { x: touch.clientX, y: touch.clientY };
    endRef.current = null;

    if (!latest.current.dragFollow) return;
    // Zoomed viewport: the finger is panning the photo under the lens. No
    // follow, no depression — the surface should feel inert to this gesture.
    if (isViewportZoomed()) return;
    const el = e.currentTarget || null;
    elRef.current = el;
    axisRef.current = null;
    prevMoveRef.current = { x: touch.clientX, t: eventTime(e) };
    lastMoveRef.current = prevMoveRef.current;
    if (el) {
      pressTimerRef.current = setTimeout(() => {
        el.style.setProperty('--swipe-press', PRESS_SCALE);
      }, PRESS_DELAY_MS);
    }
  }, [resetDragArtifacts]);

  const onTouchMove = useCallback((e) => {
    if (!latest.current.enabled || !startRef.current) return;
    if (e.touches.length > 1) {
      startRef.current = null;
      if (latest.current.dragFollow) resetDragArtifacts();
      return;
    }
    const touch = e.targetTouches[0];
    endRef.current = { x: touch.clientX, y: touch.clientY };

    if (!latest.current.dragFollow) return;
    const el = elRef.current;
    if (!el) return;

    const followX = touch.clientX - startRef.current.x;
    const followY = touch.clientY - startRef.current.y;
    prevMoveRef.current = lastMoveRef.current;
    lastMoveRef.current = { x: touch.clientX, t: eventTime(e) };

    if (axisRef.current === null) {
      if (Math.abs(followX) > LOCK_AXIS_PX && Math.abs(followX) > Math.abs(followY) * DIRECTION_RATIO) {
        axisRef.current = 'x';
        // A locked drag is a deliberate press — no reason to keep it waiting.
        if (pressTimerRef.current) {
          clearTimeout(pressTimerRef.current);
          pressTimerRef.current = null;
        }
        el.style.setProperty('--swipe-press', PRESS_SCALE);
        el.style.setProperty('--swipe-snap-ms', '0ms');
      } else if (Math.abs(followY) > LOCK_AXIS_PX) {
        // The scroller's gesture. Withdraw the pending depression so a scroll
        // passing over the card doesn't hold it pressed all the way down.
        axisRef.current = 'y';
        if (pressTimerRef.current) {
          clearTimeout(pressTimerRef.current);
          pressTimerRef.current = null;
        }
        el.style.removeProperty('--swipe-press');
      }
    }

    if (axisRef.current === 'x') {
      let offset = followX;
      if (offset < 0 && !latest.current.canSwipeLeft) offset *= EDGE_RESISTANCE;
      if (offset > 0 && !latest.current.canSwipeRight) offset *= EDGE_RESISTANCE;
      el.style.setProperty('--swipe-x', `${offset}px`);
    }
  }, [resetDragArtifacts]);

  const onTouchEnd = useCallback((e) => {
    const {
      onSwipeLeft: left, onSwipeRight: right, enabled: on, minDistance: min,
      dragFollow: follow, canSwipeLeft: canLeft, canSwipeRight: canRight
    } = latest.current;
    const start = startRef.current;
    const end = endRef.current;
    startRef.current = null;
    endRef.current = null;

    if (follow) {
      const axis = axisRef.current;
      const prev = prevMoveRef.current;
      const last = lastMoveRef.current;
      // Clearing the vars and (in the same task) advancing the slide is the
      // settle animation: the slide's transitionDuration falls back from 0ms,
      // and its transform moves from the dragged position to the new base —
      // one style recalc sees both, so it animates instead of jumping. A
      // release that doesn't page has only the vars change, which is the
      // spring back.
      resetDragArtifacts();
      if (!on || !start || !end || axis !== 'x') return;
      if (isViewportZoomed()) return;

      const dx = start.x - end.x; // >0: finger moved left, asking for next
      const dy = start.y - end.y;
      // The axis lock is decided on the gesture's FIRST few pixels and never
      // revisited, so a flick that starts level and then plunges into a feed
      // scroll still holds the 'x' lock. Re-apply the direction gate over the
      // whole gesture at release — same rule as the classic path — so that
      // diagonal scroll springs back instead of also paging the photo.
      if (Math.abs(dx) < Math.abs(dy) * DIRECTION_RATIO) return;

      let velocity = 0; // px/ms, signed the way the finger moved
      if (prev && last && last.t > prev.t && eventTime(e) - last.t <= FLICK_MAX_SAMPLE_AGE_MS) {
        velocity = (last.x - prev.x) / (last.t - prev.t);
      }
      const travels = Math.abs(dx) >= min;
      const flicks = Math.abs(dx) >= FLICK_MIN_PX
        && Math.abs(velocity) >= FLICK_VELOCITY
        && (velocity < 0) === (dx > 0);
      if (!travels && !flicks) return;

      if (dx > 0) {
        if (canLeft) left?.();
      } else if (canRight) {
        right?.();
      }
      return;
    }

    if (!on || !start || !end) return;

    // Pinch-zoomed: the drag is the user moving the photo under the lens, not
    // asking for the next one. Paging here yanks away the thing they were
    // reading — and on a surface still carrying the touch-action guard the pan
    // is refused as well, so the drag would page and do nothing else.
    if (isViewportZoomed()) return;

    const dx = start.x - end.x;
    const dy = start.y - end.y;
    if (Math.abs(dx) < min) return;
    // Vertical-dominant gesture: the user is scrolling the page past a photo,
    // not paging the photo.
    if (Math.abs(dx) < Math.abs(dy) * DIRECTION_RATIO) return;

    if (dx > 0) left?.();
    else right?.();
  }, [resetDragArtifacts]);

  // A cancelled touch (the browser or the OS claiming the gesture, a system
  // edge swipe) is not a swipe. Clearing here means the abandoned start can't
  // pair with a later move to fire one.
  const onTouchCancel = useCallback(() => {
    startRef.current = null;
    endRef.current = null;
    if (latest.current.dragFollow) resetDragArtifacts();
  }, [resetDragArtifacts]);

  // Two exits the touch handlers can never see, both of which would strand a
  // live drag's CSS properties (or a pending press timer) on the DOM:
  //  - the component unmounting mid-gesture (feed refresh, navigation);
  //  - `enabled` flipping false mid-gesture. That one is sneaky: when a
  //    refetch empties a carousel's photos, PhotoCarousel re-renders into a
  //    branch WITHOUT these handlers, but every branch returns a div in the
  //    same position, so React reuses the DOM node — nothing unmounts, no
  //    touchend can reach us, and the imperatively-set --swipe-* properties
  //    would survive on the node until the next full touch. Resetting on the
  //    disable edge clears them while elRef still points at that node.
  useEffect(() => {
    if (!enabled) resetDragArtifacts();
    return () => resetDragArtifacts();
  }, [enabled, resetDragArtifacts]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    // Only while the gesture is live: a single-photo surface has nothing to
    // defend and must stay transparent to an ancestor strip's scroll.
    style: enabled && guardNestedScroll ? SWIPE_TOUCH_ACTION : undefined
  };
}
